import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable ,  Subject } from 'rxjs';
import { ExceptionService, PaginatedList, UtilsService } from '../../../xform-compat';
import { RmsBasicCrudService } from '../../components/rms-basic-crud.service';
import { CompanyFinancial, CompanyPricing, Currency, PriceTargetsComps } from '../../../shared/models';


/**
 * Injectable service to handle company financial information
 */
@Injectable({
  providedIn: 'root'
})
export class FinancialService extends RmsBasicCrudService<any> {
  private financialUpdate: Subject<any>;

  constructor(
    http: HttpClient,
    utilsService: UtilsService,
    exceptionService: ExceptionService
  ) {
    super(http, utilsService, exceptionService);
    this.financialUpdate = new Subject<any>();
  }

  /**
   * Gets the company financial information
   * @param {number} companyId
   * @returns {Observable<CompanyFinancial>}
   */
  public getFinancial(companyId: number): Observable<CompanyFinancial> {
    return super.getData(`/api/rms/companies/${companyId}/financials?extrafield=priceTargets&extrafield=fsData&extrafield=updatedUser`);
  }

  /**
   * Get price target tied to comps
   * @param companyId
   */
  public getPriceTiedToComp(companyId: number): Observable<PriceTargetsComps> {
    return super.getData(`/api/rms/companies/${companyId}/financials/comparator`);
  }

  /**
   * Save price target tied to comps
   * @param companyId
   * @param body
   */
  public savePriceTiedToComp(companyId: number, body: Partial<PriceTargetsComps>): Observable<PriceTargetsComps> {
    return super.updateData(`/api/rms/companies/${companyId}/financials/comparator`, body);
  }

  /**
   * Gets the list of available currencies
   * @returns {Observable<Array<Currency>>}
   */
  public getCurrencies(): Observable<Array<Currency>> {
    return this.getData('/api/rms/currencies');
  }

  /**
   * Gets the pricing info for the company
   * @param {number} companyId
   * @returns {Observable<PaginatedList<CompanyPricing>>}
   */
  public getCompanyPricing (companyId: number): Observable<PaginatedList<CompanyPricing>> {
    const pricingTypes = [
      'OPEN_MARKET',
      'FIFTY_TWO_WEEK_HIGH',
      'FIFTY_TWO_WEEK_LOW',
      'PRE_MARKET',
      'POST_MARKET',
    ];
    return this.getData(
      `/api/rms/companies/${companyId}/pricings?pricingTypes=${pricingTypes.join('&pricingTypes=')}`
    );
  }

  /**
   * Updates the company financial information
   * @param {number} companyId
   * @param body
   * @returns {Observable<CompanyFinancial>}
   */
  public updateCompanyFinancial (companyId: number, body: any): Observable<CompanyFinancial> {
    return this.updateData(`/api/rms/companies/${companyId}/financials`, body);
  }

  /**
   * Sends a message to the subscribed object on the component
   * @param message
   */
  public sendMessage(message: string) {
    this.financialUpdate.next({ text: message });
  }

  /**
   * Observable to listen when a message is sent
   */
  public getMessage(): Observable<any> {
    return this.financialUpdate.asObservable();
  }

}
